// Bac à sable — accès au contrat `/api/herbo-admin/bac-a-sable/*`.
//
// Le refresh du bac à sable (scripts/sandbox/refresh-sandbox.sh) recharge la
// base locale depuis la PROD en lecture seule. C'est une opération LONGUE
// (15-25 min) pendant laquelle l'application peut afficher des erreurs. Le
// shell n'en pilote que le déclenchement et l'affichage de l'avancement ;
// toute la mécanique vit côté API (module herbo-admin).
//
// Contrat FERME (implémenté en parallèle côté herbo-admin) :
//   GET  /api/herbo-admin/bac-a-sable/statut  → StatutBacASable
//   POST /api/herbo-admin/bac-a-sable/refresh → 202 | 403 | 409
//
// Tant que l'API n'est pas déployée, le GET répond 404 : le shell doit alors
// se taire complètement (pas de chip cassé, pas d'erreur console, pas de
// boucle de retry). D'où le sentinel `null` renvoyé par la query.

import { z } from 'zod';
import { ApiError, api } from './api-client';
import { apiBaseUrl, tenant } from './tenant';

/**
 * Nullabilité tolérée sur tous les champs optionnels du contrat : un bac à
 * sable jamais rafraîchi n'a ni `donneesAu`, ni `startedAt`, ni étape. Refuser
 * ces `null` ferait échouer zod et effacerait le contrôle sans le dire — le
 * pire des deux mondes. On lit large, on affiche prudemment.
 */
export const StatutBacASableSchema = z.object({
  sandbox: z.boolean(),
  running: z.boolean(),
  startedAt: z.string().nullable().optional().default(null),
  exitCode: z.number().nullable().optional().default(null),
  derniereEtape: z.string().nullable().optional().default(null),
  donneesAu: z.string().nullable().optional().default(null),
  // Volontairement NON borné ici : le pourcentage est cosmétique et l'API le
  // déduit d'un log. Une valeur hors [0-100] doit être ramenée dans les clous
  // (cf. `normaliserPct`), surtout pas faire échouer la lecture du statut et
  // effacer le contrôle pour autant.
  progressionPct: z.number().nullable().optional().default(null),
});
export type StatutBacASable = z.infer<typeof StatutBacASableSchema>;

/** Ramène un pourcentage dans [0-100] entier ; `null` si absent ou illisible. */
export function normaliserPct(valeur: number | null | undefined): number | null {
  if (valeur === null || valeur === undefined || !Number.isFinite(valeur)) return null;
  return Math.min(100, Math.max(0, Math.round(valeur)));
}

export const CLE_QUERY_STATUT = ['shell', 'bac-a-sable', 'statut'] as const;

/**
 * Récupère le statut. Renvoie `null` — et non une erreur — quand l'endpoint
 * n'existe pas encore (404) ou que l'utilisateur n'y a pas droit (403) :
 * dans les deux cas le contrôle doit simplement ne pas exister à l'écran.
 */
export async function chargerStatutBacASable(signal?: AbortSignal): Promise<StatutBacASable | null> {
  try {
    return await api.get('/herbo-admin/bac-a-sable/statut', StatutBacASableSchema, { signal });
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 403)) return null;
    throw err;
  }
}

export type RaisonRefusRefresh = 'droits' | 'deja-en-cours' | 'indisponible' | 'reseau';
export type ResultatRefresh = { readonly ok: true } | { readonly ok: false; readonly raison: RaisonRefusRefresh };

/** Message français prêt à afficher — jamais de code HTTP à l'écran. */
export function messageRefusRefresh(raison: RaisonRefusRefresh): string {
  switch (raison) {
    case 'droits':
      return "Vous n'avez pas les droits pour lancer un rafraîchissement.";
    case 'deja-en-cours':
      return 'Un rafraîchissement est déjà en cours.';
    case 'indisponible':
      return "Le rafraîchissement n'est pas disponible sur cette installation.";
    case 'reseau':
      return 'Impossible de joindre le serveur. Réessayez dans un instant.';
  }
}

/**
 * Déclenche le refresh. Le contrat définit la réponse par son STATUT
 * (202/403/409), pas par un corps : on ne parse donc aucun corps — un 202 sans
 * body ferait échouer le client zod partagé alors que tout va bien.
 */
export async function lancerRefreshBacASable(): Promise<ResultatRefresh> {
  const base = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
  let reponse: Response;
  try {
    reponse = await fetch(`${base}/herbo-admin/bac-a-sable/refresh`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Tenant-Id': tenant.id },
      credentials: 'include',
    });
  } catch {
    return { ok: false, raison: 'reseau' };
  }
  if (reponse.status === 202 || reponse.ok) return { ok: true };
  if (reponse.status === 403) return { ok: false, raison: 'droits' };
  if (reponse.status === 409) return { ok: false, raison: 'deja-en-cours' };
  if (reponse.status === 404) return { ok: false, raison: 'indisponible' };
  return { ok: false, raison: 'reseau' };
}

// ─── Formatage (heure de Bruxelles, quel que soit le fuseau du poste) ────────
const fmtJour = new Intl.DateTimeFormat('fr-BE', { timeZone: 'Europe/Brussels', day: '2-digit', month: '2-digit' });
const fmtHeure = new Intl.DateTimeFormat('fr-BE', {
  timeZone: 'Europe/Brussels',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** `2026-08-09T21:34:00Z` → `09/08 23:34` (Bruxelles). `null` si date absente/illisible. */
export function formaterDonneesAu(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${fmtJour.format(d)} ${fmtHeure.format(d)}`;
}
